/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Requirements` for the Caintra Connect frontend.
 * @module pages/Requirements
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/Requirements.module.css";

import { AppContext } from "../context/AppContext";
import { getCompanyByRFC } from "../logic/company/index.js";
import {
  getCompanyUnapprovedRequirements,
  approveRequirement,
  deleteRequirement,
  updateRequirement,
  LOCALSTORAGE_USER_ID_KEY,
} from "../logic/requirements";
import {
  loadRequirementsByTabIndex,
  loadRequirementsPageData,
  loadCurrentRequirementsUser,
  resolveRequirementNavigationTarget,
  logRequirementsPageDataError,
} from "../logic/requirements/requirementsPageData.js";
import {
  getRequirementsViewportFlags,
  shouldUseRequirementsMobileView,
  syncRequirementsResponsivePanels,
} from "../utils/requirements/responsivePanelSync.js";
import { use, useCallback, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";

import useSessionUserRefresh from "../hooks/useSessionUserRefresh";
import {
  RequirementsDialogs,
  RequirementsHeader,
  RequirementsListPanel,
  RequirementsStage,
} from "./RequirementsSections.jsx";
import { canAccessRequirements } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

const DEFAULT_REQUIREMENTS_PAGE_SIZE = 10;
const REQUIREMENTS_TAB_ALL = 0;
const REQUIREMENTS_TAB_MINE = 1;
const REQUIREMENTS_TAB_PENDING = 2;

/**
 * Requirements page: list, create, and manage requirements.
 * @returns {JSX.Element}
 */
const Requirements = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canAccessRequirements);
  const confirmationDialogRef = useRef(null);
  const [rejectConfirmationOpen, setRejectConfirmationOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [requirementsData, setRequirementsData] = useState([]);
  const [tabIndex, setTabIndex] = useState(REQUIREMENTS_TAB_ALL);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(
    DEFAULT_REQUIREMENTS_PAGE_SIZE,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [proposalsModalOpen, setProposalsModalOpen] = useState(false);
  const [selectedRequirementForProposals, setSelectedRequirementForProposals] =
    useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  useSessionUserRefresh(setCurrentUser, {
    refreshPermissions: true,
    refreshOnMount: false,
  });
  const [userCompany, setUserCompany] = useState(null);
  /** True once we know company (or that user has no company), so create button visibility is final */
  const [companyPermissionResolved, setCompanyPermissionResolved] =
    useState(false);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [showRequirementDetails, setShowRequirementDetails] = useState(false);
  const [showRequirementCreation, setShowRequirementCreation] = useState(false);
  const [showProposalsList, setShowProposalsList] = useState(false);

  const [isDesktop, setIsDesktop] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isMediumMobile, setIsMediumMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      getData();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      return () => {};
    }
    if (!currentUser?.RFC) {
      setUserCompany(null);
      setCompanyPermissionResolved(true);
      return () => {};
    }
    setCompanyPermissionResolved(false);
    let cancelled = false;
    const fetchCompany = async () => {
      const company = await getCompanyByRFC(currentUser.RFC, navigate);
      if (!cancelled && company) {
        setUserCompany(company);
      } else if (!cancelled) {
        setUserCompany(null);
      } else {
        // Request cancelled before company fetch completed.
      }
      if (!cancelled) {
        setCompanyPermissionResolved(true);
      }
    };
    fetchCompany();
    return () => {
      cancelled = true;
    };
  }, [allowed, currentUser?.RFC, navigate]);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const viewportFlags = getRequirementsViewportFlags(width);
      setIsDesktop(viewportFlags.isDesktop);
      setIsTablet(viewportFlags.isTablet);
      setIsMediumMobile(viewportFlags.isMediumMobile);
      setIsSmallMobile(viewportFlags.isSmallMobile);

      syncRequirementsResponsivePanels({
        width,
        showRequirementCreation,
        createModalOpen,
        showRequirementDetails,
        displayModalOpen,
        showProposalsList,
        proposalsModalOpen,
        setShowRequirementCreation,
        setCreateModalOpen,
        setShowRequirementDetails,
        setDisplayModalOpen,
        setShowProposalsList,
        setProposalsModalOpen,
      });
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [
    showRequirementCreation,
    showRequirementDetails,
    showProposalsList,
    createModalOpen,
    displayModalOpen,
    proposalsModalOpen,
  ]);

  useEffect(() => {
    const navigationTarget = resolveRequirementNavigationTarget({
      locationState: location.state,
      requirementsData,
    });

    if (navigationTarget.type === "selectedRequirement") {
      setSelectedRequirement(navigationTarget.requirement);
      setDisplayModalOpen(true);
      setCurrentUser(navigationTarget.currentUser);
      navigate(location.pathname, { replace: true });
      return;
    }

    if (
      navigationTarget.type === "objectId" ||
      navigationTarget.type === "notFound"
    ) {
      if (navigationTarget.requirement) {
        setSelectedRequirement(navigationTarget.requirement);
        setDisplayModalOpen(true);
      }
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname, requirementsData]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const pageData = await loadRequirementsPageData({
        navigate,
        changePermissions,
      });
      setSectors(pageData.sectors);
      setCertifications(pageData.certifications);
      setCategories(pageData.categories);
      setRequirementsData(pageData.requirements);
      const userData = await loadCurrentRequirementsUser(navigate);
      if (userData) {
        setCurrentUser(userData);
      }
    } catch (error) {
      logRequirementsPageDataError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRequirementsList = async (nextTabIndex) => {
    try {
      const requirements = await loadRequirementsByTabIndex(
        nextTabIndex,
        navigate,
      );
      if (requirements) {
        setRequirementsData(requirements);
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
    setTabIndex(nextTabIndex);
  };

  const handleSearch = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const handleAddRequirement = async () => {
    if (tabIndex === REQUIREMENTS_TAB_PENDING && currentUser?.esColaborador) {
      const unapprovedRequirements = await getCompanyUnapprovedRequirements(
        localStorage.getItem(LOCALSTORAGE_USER_ID_KEY),
        navigate,
      );
      if (unapprovedRequirements) {
        setRequirementsData(unapprovedRequirements);
      }
    }
  };

  const handleApproveRequirement = async (requirementId) => {
    const data = await approveRequirement(
      requirementId,
      currentUser?._id,
      navigate,
    );
    if (data) {
      // remove requirement from requirementsData
      setRequirementsData((prevRequirements) =>
        prevRequirements.filter(
          (requirement) => requirement._id !== requirementId,
        ),
      );
    } else {
      logger.error("Error approving requirement");
    }
  };

  const handleRejectRequirement = async (requirement) => {
    setSelectedRequirement(requirement);
    setRejectConfirmationOpen(true);
  };

  const handleRejectConfirmation = async (rejectionReason) => {
    setIsRejecting(true);
    try {
      const data = await deleteRequirement(
        selectedRequirement?._id,
        rejectionReason,
        navigate,
      );
      if (data) {
        setRequirementsData((prevRequirements) =>
          prevRequirements.filter(
            (requirement) => requirement._id !== selectedRequirement?._id,
          ),
        );
      }
    } catch (error) {
      logger.error("Error rejecting requirement:", { err: sanitizeError(error) });
    } finally {
      setIsRejecting(false);
      setSelectedRequirement(null);
      setRejectConfirmationOpen(false);
    }
  };

  const handleToggleActive = async (requirementIndex) => {
    let requirementId = null;
    const newRequirements = requirementsData.map((req, i) => {
      if (i === requirementIndex) {
        requirementId = req._id;
        return {
          ...req,
          activo: !req.activo,
        };
      } else return req;
    });
    if (requirementId) {
      try {
        await updateRequirement(
          requirementId,
          { activo: !requirementsData[requirementIndex].activo },
          navigate,
        );
      } catch (error) {
        logger.error("Error updating requirement:", { err: sanitizeError(error) });
      }
    }

    setRequirementsData(newRequirements);
  };

  const handleBackToList = () => {
    setShowRequirementCreation(false);
    setCreateModalOpen(false);

    setShowRequirementDetails(false);
    setDisplayModalOpen(false);

    setSelectedRequirement(null);
    setSelectedRequirementForProposals(null);

    setShowProposalsList(false);
    setProposalsModalOpen(false);
  };

  const filteredRequirementsList = requirementsData?.filter((requirement) =>
    requirement.titulo
      ? requirement.titulo.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const paginatedRequirementsList = filteredRequirementsList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRequirementsList.length / resultsLimit),
  );

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const showCreateRequirementButton =
    companyPermissionResolved &&
    (userCompany == null || userCompany.crearRequerimientos !== false);

  const isMobileRequirementsLayout = shouldUseRequirementsMobileView({
    isTablet,
    isMediumMobile,
    isSmallMobile,
  });

  const handleCreateRequirementClick = () => {
    if (isMobileRequirementsLayout) {
      setShowRequirementCreation(true);
    } else {
      setCreateModalOpen(true);
    }
  };

  const handleViewRequirement = (requirement) => {
    setSelectedRequirement(requirement);
    if (isMobileRequirementsLayout) {
      setShowRequirementDetails(true);
    } else {
      setDisplayModalOpen(true);
    }
  };

  const handleViewProposals = (requirement) => {
    setSelectedRequirementForProposals(requirement);
    if (isMobileRequirementsLayout) {
      setShowProposalsList(true);
    } else {
      setProposalsModalOpen(true);
    }
  };

  const closeCreateModal = () => setCreateModalOpen(false);
  const closeDisplayModal = () => setDisplayModalOpen(false);
  const closeProposalsModal = () => {
    setProposalsModalOpen(false);
    setSelectedRequirementForProposals(null);
  };
  const closeRejectModal = () => {
    setSelectedRequirement(null);
    setRejectConfirmationOpen(false);
  };
  const showAllRequirements = () =>
    handleChangeRequirementsList(REQUIREMENTS_TAB_ALL);
  const showMyRequirements = () =>
    handleChangeRequirementsList(REQUIREMENTS_TAB_MINE);
  const showPendingRequirements = () =>
    handleChangeRequirementsList(REQUIREMENTS_TAB_PENDING);

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <RequirementsHeader
        t={t}
        navigate={navigate}
        showRequirementDetails={showRequirementDetails}
        showRequirementCreation={showRequirementCreation}
        showProposalsList={showProposalsList}
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        currentUser={currentUser}
        showCreateRequirementButton={showCreateRequirementButton}
        onCreateRequirement={handleCreateRequirementClick}
      />
      <RequirementsStage
        showRequirementDetails={showRequirementDetails}
        selectedRequirement={selectedRequirement}
        currentUser={currentUser}
        onBack={handleBackToList}
        showRequirementCreation={showRequirementCreation}
        categories={categories}
        sectors={sectors}
        certifications={certifications}
        onCreateRequirement={handleAddRequirement}
        showProposalsList={showProposalsList}
        selectedRequirementForProposals={selectedRequirementForProposals}
        list={
          <RequirementsListPanel
            t={t}
            tabIndex={tabIndex}
            onShowAll={showAllRequirements}
            onShowMine={showMyRequirements}
            onShowPending={showPendingRequirements}
            currentUser={currentUser}
            paginatedRequirementsList={paginatedRequirementsList}
            showCreateRequirementButton={showCreateRequirementButton}
            isDesktop={isDesktop}
            onViewRequirement={handleViewRequirement}
            onViewProposals={handleViewProposals}
            onRejectRequirement={handleRejectRequirement}
            onApproveRequirement={handleApproveRequirement}
            onToggleActive={handleToggleActive}
            onResultsLimitChange={handleResultsLimitChange}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        }
      />
      <RequirementsDialogs
        categories={categories}
        sectors={sectors}
        certifications={certifications}
        createModalOpen={createModalOpen}
        onCloseCreate={closeCreateModal}
        onCreateRequirement={handleAddRequirement}
        displayModalOpen={displayModalOpen}
        onCloseDisplay={closeDisplayModal}
        selectedRequirement={selectedRequirement}
        currentUser={currentUser}
        proposalsModalOpen={proposalsModalOpen}
        selectedRequirementForProposals={selectedRequirementForProposals}
        onCloseProposals={closeProposalsModal}
        rejectConfirmationOpen={rejectConfirmationOpen}
        confirmationDialogRef={confirmationDialogRef}
        onCloseReject={closeRejectModal}
        onConfirmReject={handleRejectConfirmation}
        isRejecting={isRejecting}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Requirements;
